import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Lead, CreateLeadDTO, Origem, Indicador } from '@/types/sales';
import { salesService } from '@/services/salesService';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead?: Lead | null;
  onSave: () => void;
}

const LeadModal: React.FC<LeadModalProps> = ({ isOpen, onClose, lead, onSave }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [origens, setOrigens] = useState<Origem[]>([]);
  const [indicadores, setIndicadores] = useState<Indicador[]>([]);
  const [origemSearch, setOrigemSearch] = useState('');
  const [indicadorSearch, setIndicadorSearch] = useState('');
  
  // Form state
  const [formData, setFormData] = useState<CreateLeadDTO>({
    cnpj: '',
    nome_empresa: '',
    telefone: '',
    email: '',
    is_indicacao: false,
    vendedor_id: user?.id || 0
  });

  // Carregar origens e indicadores
  useEffect(() => {
    if (isOpen) {
      loadOrigens();
      loadIndicadores();
      
      // Se é edição, preencher form
      if (lead) {
        setFormData({
          cnpj: lead.cnpj,
          nome_empresa: lead.nome_empresa,
          telefone: lead.telefone,
          email: lead.email,
          origem_id: lead.origem_id,
          is_indicacao: lead.is_indicacao,
          indicador_id: lead.indicador_id,
          vendedor_id: lead.vendedor_id
        });
      } else {
        // Reset para novo lead
        setFormData({
          cnpj: '',
          nome_empresa: '',
          telefone: '',
          email: '',
          is_indicacao: false,
          vendedor_id: user?.id || 0
        });
      }
    }
  }, [isOpen, lead, user]);

  const loadOrigens = async () => {
    try {
      const data = await salesService.getOrigens();
      setOrigens(data);
    } catch (error) {
      console.error('Erro ao carregar origens:', error);
    }
  };

  const loadIndicadores = async () => {
    try {
      const data = await salesService.getIndicadores();
      setIndicadores(data);
    } catch (error) {
      console.error('Erro ao carregar indicadores:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validações
    if (!formData.cnpj || !formData.nome_empresa || !formData.telefone || !formData.email) {
      toast({
        title: 'Erro',
        description: 'Preencha todos os campos obrigatórios',
        variant: 'destructive'
      });
      return;
    }

    if (formData.is_indicacao && !formData.indicador_id && !formData.indicador_nome) {
      toast({
        title: 'Erro',
        description: 'Informe o indicador',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsLoading(true);
      
      if (lead) {
        // Atualizar
        await salesService.updateLead(lead.id, formData);
        toast({
          title: 'Sucesso',
          description: 'Lead atualizado com sucesso'
        });
      } else {
        // Criar
        await salesService.createLead(formData);
        toast({
          title: 'Sucesso',
          description: 'Lead criado com sucesso'
        });
      }
      
      onSave();
      
    } catch (error: any) {
      console.error('Erro ao salvar lead:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Erro ao salvar lead',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{lead ? 'Editar Lead' : 'Novo Lead'}</DialogTitle>
          <DialogDescription>
            {lead ? 'Atualize os dados do lead' : 'Preencha os dados do novo lead'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* CNPJ */}
            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ *</Label>
              <Input
                id="cnpj"
                value={formData.cnpj}
                onChange={(e) => setFormData(prev => ({ ...prev, cnpj: e.target.value }))}
                placeholder="00.000.000/0000-00"
                required
              />
            </div>

            {/* Nome da Empresa */}
            <div className="space-y-2">
              <Label htmlFor="nome_empresa">Nome da Empresa *</Label>
              <Input
                id="nome_empresa"
                value={formData.nome_empresa}
                onChange={(e) => setFormData(prev => ({ ...prev, nome_empresa: e.target.value }))}
                placeholder="Ex: Átomos Tecnologia"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Telefone */}
            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone *</Label>
              <Input
                id="telefone"
                value={formData.telefone}
                onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                placeholder="(00) 00000-0000"
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">E-mail *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contato@empresa.com"
                required
              />
            </div>
          </div>

          {/* Origem */}
          <div className="space-y-2">
            <Label htmlFor="origem">Origem *</Label>
            <Select
              value={formData.origem_id?.toString() || ''}
              onValueChange={(value) => setFormData(prev => ({ 
                ...prev, 
                origem_id: parseInt(value),
                origem_nome: undefined 
              }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a origem do lead" />
              </SelectTrigger>
              <SelectContent>
                {origens.map(origem => (
                  <SelectItem key={origem.id} value={origem.id.toString()}>
                    {origem.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Cadastre novas origens em Configurações Comerciais
            </p>
          </div>

          {/* Indicação */}
          <div className="flex items-center space-x-2">
            <Switch
              id="is_indicacao"
              checked={formData.is_indicacao}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_indicacao: checked }))}
            />
            <Label htmlFor="is_indicacao" className="cursor-pointer">
              Este lead foi indicado por alguém
            </Label>
          </div>

          {/* Indicador (se for indicação) */}
          {formData.is_indicacao && (
            <div className="space-y-4 p-4 border rounded-lg bg-green-50">
              <div className="space-y-2">
                <Label htmlFor="indicador">Indicador *</Label>
                <Select
                  value={formData.indicador_id?.toString() || ''}
                  onValueChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    indicador_id: parseInt(value),
                    indicador_nome: undefined 
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione quem indicou o lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {indicadores.map(indicador => (
                      <SelectItem key={indicador.id} value={indicador.id.toString()}>
                        {indicador.nome} {indicador.telefone && `(${indicador.telefone})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Cadastre novos indicadores em Configurações Comerciais
                </p>
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="bg-blue-600 hover:bg-blue-700">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {lead ? 'Atualizar' : 'Criar Lead'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadModal;


